/**
 * Hotspot Image — non-modal dialog product previews on an image canvas.
 *
 * Defines the `<fit-hotspots-block>` custom element.
 *
 * Interaction contract:
 * - Click a [data-hotspot-trigger] pin to open its linked <dialog>
 * - Click [data-hotspot-close], the backdrop, or press Escape to close
 * - Focus moves into the panel on open, returns to the trigger on close
 * - Only one panel can be open at a time per block instance
 * - Uses dialog.show() (non-modal) — no scroll lock, no focus trap
 */
(function () {
  'use strict';

  if (customElements.get('fit-hotspots-block')) return;

  class FitHotspots extends HTMLElement {
    #ac = null;
    #activePanel = null;
    #activeTrigger = null;

    connectedCallback() {
      this.#ac = new AbortController();
      var signal = this.#ac.signal;

      this.addEventListener('click', this.#onClick, { signal: signal });

      document.addEventListener(
        'keydown',
        function (e) {
          if (e.key === 'Escape' && this.#activePanel) {
            e.preventDefault();
            this.#close();
          }
        }.bind(this),
        { signal: signal }
      );
    }

    disconnectedCallback() {
      if (this.#ac) {
        this.#ac.abort();
        this.#ac = null;
      }
      this.#activePanel = null;
      this.#activeTrigger = null;
    }

    #onClick = function (e) {
      /* Close button */
      if (e.target.closest('[data-hotspot-close]')) {
        e.stopPropagation();
        this.#close();
        return;
      }

      /* Trigger button */
      var trigger = e.target.closest('[data-hotspot-trigger]');
      if (trigger) {
        e.stopPropagation();
        var controlsId = trigger.getAttribute('aria-controls');
        var panel = controlsId
          ? this.querySelector('#' + CSS.escape(controlsId))
          : null;
        if (!panel) return;

        if (this.#activePanel === panel) {
          this.#close();
        } else {
          this.#closeImmediate();
          this.#open(trigger, panel);
        }
        return;
      }

      /* Click on canvas backdrop (outside any panel) */
      if (this.#activePanel && !e.target.closest('.fit-hotspot__panel')) {
        this.#close();
      }
    }.bind(this);

    #open(trigger, panel) {
      var canvas = this.querySelector('.fit-hotspot__canvas');

      panel.show();
      this.#activePanel = panel;
      this.#activeTrigger = trigger;
      trigger.setAttribute('aria-expanded', 'true');

      if (canvas) canvas.classList.add('fit-hotspot__canvas--overlay');

      /* Move focus to first focusable element inside the panel */
      var focusTarget = panel.querySelector(
        'button, a[href], [tabindex]:not([tabindex="-1"])'
      );
      if (focusTarget) {
        requestAnimationFrame(function () {
          focusTarget.focus();
        });
      }
    }

    #close() {
      if (!this.#activePanel) return;

      var panel = this.#activePanel;
      var trigger = this.#activeTrigger;
      var canvas = this.querySelector('.fit-hotspot__canvas');

      this.#activePanel = null;
      this.#activeTrigger = null;

      panel.classList.add('fit-hotspot__panel--closing');
      if (canvas) canvas.classList.remove('fit-hotspot__canvas--overlay');

      var cleaned = false;
      var cleanup = function () {
        if (cleaned) return;
        cleaned = true;
        panel.classList.remove('fit-hotspot__panel--closing');
        panel.close();
        if (trigger) {
          trigger.setAttribute('aria-expanded', 'false');
          trigger.focus();
        }
      };

      panel.addEventListener('animationend', cleanup, { once: true });
      /* Fallback if animation doesn't fire (e.g. reduced motion) */
      setTimeout(cleanup, 300);
    }

    #closeImmediate() {
      if (!this.#activePanel) return;

      var panel = this.#activePanel;
      var trigger = this.#activeTrigger;
      var canvas = this.querySelector('.fit-hotspot__canvas');

      panel.classList.remove('fit-hotspot__panel--closing');
      panel.close();
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      if (canvas) canvas.classList.remove('fit-hotspot__canvas--overlay');

      this.#activePanel = null;
      this.#activeTrigger = null;
    }
  }

  customElements.define('fit-hotspots-block', FitHotspots);
})();
